import { API_ORIGIN, apiUrl } from "./apiOrigin";
// src/cqcApi.js
// Frontend API client for the CQC module — same shape as SiteManagement.js's
// `api()` helper and RegulatoryCompliance.js's direct fetch calls: Bearer
// token from localStorage, JSON in/out, org scoping handled server-side from
// the token so nothing extra needs to be passed from here.

const API_BASE = apiUrl(""); // same origin as the rest of the app; cqc.js mounts at /api/cqc

async function call(method, path, body) {
  const token = localStorage.getItem("authToken");
  const opts = {
    method,
    headers: {
      Authorization: `Bearer ${token}`
    }
  };
  if (body !== undefined) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE}/api/cqc${path}`, opts);
  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch (_) {
    data = {
      error: text
    };
  }
  if (!res.ok) throw new Error(data.error || `HTTP ${res.status}`);
  return data;
}
export const cqcApi = {
  searchLocations: (name, postcode) => call("GET", `/search/locations?${new URLSearchParams({
    name: name || "",
    postcode: postcode || ""
  })}`),
  searchProviders: (name, postcode) => call("GET", `/search/providers?${new URLSearchParams({
    name: name || "",
    postcode: postcode || ""
  })}`),
  resolveCountry: postcode => call("POST", "/location/resolve", {
    postcode
  }),
  createOrganisation: payload => call("POST", "/sites/manual", payload),
  saveAddress: (siteId, address, expectedRevision) => call("POST", `/sites/${siteId}/address`, {
    address,
    expectedRevision
  }),
  importSites: locationIds => call("POST", "/sites/import", {
    locationIds
  }),
  listSites: () => call("GET", "/sites"),
  saveServiceProfile: (siteId, profile, expectedRevision) => call("POST", `/sites/${siteId}/profile`, {
    profile,
    expectedRevision
  }),
  resyncSite: siteId => call("POST", `/sites/${siteId}/resync`),
  listAttestations: siteId => call("GET", `/compliance/attestations${siteId ? `?siteId=${siteId}` : ""}`),
  saveAttestation: payload => call("POST", "/compliance/attestations", payload),
  listEntries: siteId => call("GET", `/compliance/entries${siteId ? `?siteId=${siteId}` : ""}`),
  addEntry: payload => call("POST", "/compliance/entries", payload),
  listApplicability: siteId => call("GET", `/compliance/applicability${siteId ? `?siteId=${siteId}` : ""}`),
  setApplicability: payload => call("POST", "/compliance/applicability", payload),
  listContributors: siteId => call("GET", `/contributors${siteId ? `?siteId=${siteId}` : ""}`),
  saveContributor: payload => call("POST", "/contributors", payload),
  listAssignments: siteId => call("GET", `/compliance/assignments${siteId ? `?siteId=${siteId}` : ""}`),
  saveAssignment: payload => call("POST", "/compliance/assignments", payload),
  // Evidence items — UPDATE 7: many proof items per element (link or
  // upload), shown as a scrollable list in the Worklist's detail panel.
  listEvidenceItems: siteId => call("GET", `/compliance/evidence-items${siteId ? `?siteId=${siteId}` : ""}`),
  addEvidenceItem: payload => call("POST", "/compliance/evidence-items", payload),
  removeEvidenceItem: evidenceItemId => call("POST", `/compliance/evidence-items/${evidenceItemId}/remove`),
  listMessages: siteId => call("GET", `/compliance/messages${siteId ? `?siteId=${siteId}` : ""}`),
  addMessage: payload => call("POST", "/compliance/messages", payload),
  inspectorOrganisations: () => call("GET", "/inspector/organisations"),
  inspectorSites: organisationName => call("GET", `/inspector/sites${organisationName ? `?organisationName=${encodeURIComponent(organisationName)}` : ""}`),
  inspectorSitePack: siteId => call("GET", `/inspector/site/${siteId}/pack`),
  // Uploaded-document view/download URL. `download` (bool) sets
  // Content-Disposition to attachment; leave it off to open inline (view)
  // in a new tab. Not a JSON call — used directly as an <a href>.
  documentUrl: (documentId, download) => `${API_BASE}/api/cqc/documents/${documentId}${download ? "?download=1" : ""}`,
  // Emails a contributor a no-login link to set this element's evidence
  // source themselves — see CqcEvidenceDialog.js's "Ask a contributor"
  // panel and CqcContributorTask.js (the page the link opens).
  sendContributorTask: payload => call("POST", "/compliance/contributor-task/send", payload)
};

// -----------------------------------------------------------------------------
// cqcTaskApi — the PUBLIC client for the contributor-task portal
// (CqcContributorTask.js). No Authorization header: the token in the URL
// (?cqcTask=TOKEN, read by App.js) is the credential, verified server-side
// on every call. Talks to /api/cqc/contributor-task/... — deliberately a
// different path prefix from cqcApi's /api/cqc/compliance/... calls above,
// so server.js's allow-list can make exactly this prefix public without
// exposing anything else.
// -----------------------------------------------------------------------------
async function publicCall(method, path, body) {
  const opts = {
    method,
    headers: {}
  };
  if (body !== undefined) {
    opts.headers["Content-Type"] = "application/json";
    opts.body = JSON.stringify(body);
  }
  const res = await fetch(`${API_BASE}/api/cqc/contributor-task${path}`, opts);
  const text = await res.text();
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch (_) {
    data = {
      error: text
    };
  }
  if (!res.ok) throw new Error(data.error || data.reason || `HTTP ${res.status}`);
  return data;
}
export const cqcTaskApi = {
  context: token => publicCall("GET", `/${encodeURIComponent(token)}`),
  setSource: (token, documentRef, source) => publicCall("POST", `/${encodeURIComponent(token)}/source`, {
    documentRef,
    source
  }),
  saveComment: (token, payload) => publicCall("POST", `/${encodeURIComponent(token)}/comment`, payload),
  listMessages: token => publicCall("GET", `/${encodeURIComponent(token)}/messages`),
  addMessage: (token, body) => publicCall("POST", `/${encodeURIComponent(token)}/message`, {
    body
  }),
  documentUrl: (token, documentId, download) => `${API_BASE}/api/cqc/contributor-task/${encodeURIComponent(token)}/document/${documentId}${download ? "?download=1" : ""}`
};
